import { useMemo } from 'react';
import type { ClassificationPageMetric } from '../pages/DocumentView';

const BUCKETS = [
  { label: '<50%', min: 0, max: 0.5, bg: 'bg-red-500' },
  { label: '50–70%', min: 0.5, max: 0.7, bg: 'bg-amber-500' },
  { label: '70–85%', min: 0.7, max: 0.85, bg: 'bg-yellow-400' },
  { label: '85%+', min: 0.85, max: Infinity, bg: 'bg-green-500' },
] as const;

function marginColor(marginPp: number): string {
  if (marginPp >= 20) return 'text-green-600 dark:text-green-400';
  if (marginPp >= 10) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

export function ClassificationPerformanceCard({ pages }: { pages: ClassificationPageMetric[] }) {
  const bucketCounts = useMemo(() => {
    const counts = BUCKETS.map(() => 0);
    for (const p of pages) {
      const idx = BUCKETS.findIndex((b) => p.confidence >= b.min && p.confidence < b.max);
      counts[idx === -1 ? BUCKETS.length - 1 : idx]++;
    }
    return counts;
  }, [pages]);

  const avgMarginPp = useMemo(() => {
    if (!pages.length) return 0;
    return (pages.reduce((sum, p) => sum + p.margin, 0) / pages.length) * 100;
  }, [pages]);

  if (!pages.length) return null;

  const total = pages.length;

  return (
    <div className="flex flex-col sm:flex-row gap-4">
      {/* Confidence Distribution */}
      <div className="flex-1 min-w-0">
        <h4 className="text-xs font-medium text-gray-500 dark:text-zinc-400 mb-2">Confidence distribution</h4>
        <div className="flex h-6 w-full overflow-hidden rounded-md">
          {BUCKETS.map((bucket, i) => {
            const count = bucketCounts[i];
            if (count === 0) return null;
            const pct = (count / total) * 100;
            return (
              <div
                key={bucket.label}
                className={`${bucket.bg} flex items-center justify-center text-[10px] font-bold text-white`}
                style={{ width: `${pct}%` }}
                title={`${bucket.label}: ${count} pages (${pct.toFixed(0)}%)`}
              >
                {pct >= 12 ? count : ''}
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-1">
          {BUCKETS.map((bucket, i) => {
            const count = bucketCounts[i];
            if (count === 0) return null;
            return (
              <span key={bucket.label} className="text-[10px] text-gray-500 dark:text-zinc-500">
                {bucket.label}: {count}
              </span>
            );
          })}
        </div>
      </div>

      {/* Decisiveness */}
      <div className="flex flex-col items-center justify-center px-4 min-w-[100px]">
        <span className={`text-2xl font-bold tabular-nums ${marginColor(avgMarginPp)}`}>
          {avgMarginPp.toFixed(0)}pp
        </span>
        <span className="text-[10px] text-gray-500 dark:text-zinc-400 text-center mt-0.5">
          Avg confidence margin
        </span>
      </div>
    </div>
  );
}
